<header class="pcd-page-header">
  <div class="pcd-title-block">
    <i class="fa-solid fa-chart-pie pcd-title-icon" aria-hidden="true"></i>
    <h1 class="pcd-page-title">Product Code Report</h1>
  </div>
  <div class="pcd-fleet-select" aria-label="Fleet selection">
    <app-dropdown
      [control]="fleetControl"
      [options]="fleetOptions"
      [label]="''"
      placeholder="Select fleet"
      dataId="product-code-distribution-fleet"
    ></app-dropdown>
  </div>
</header>

<section class="pcd-product-cards" aria-label="Product family summary">
  @for (p of products; track p.id; let idx = $index) {
  <article
    class="pcd-product-card"
    [class.pcd-product-card--selected]="isProductSelected(idx)"
    role="button"
    tabindex="0"
    [attr.aria-pressed]="isProductSelected(idx)"
    [attr.aria-label]="p.label + ', ' + (p.deviceCount | number) + ' devices'"
    (click)="selectProduct(idx, $event)"
    (keydown)="onProductKeydown(idx, $event)"
  >
    <p class="pcd-product-card__name">{{ p.label }}</p>
    <p class="pcd-product-card__count">{{ p.deviceCount | number }} devices</p>
    <div class="pcd-product-card__underline" aria-hidden="true"></div>
  </article>
  }
</section>

<div class="pcd-dashboard-panel">
  <div class="pcd-columns">
    <!-- Product Code Distribution -->
    <section class="pcd-col pcd-col--product" aria-labelledby="pcd-product-dist-title">
      <h2 id="pcd-product-dist-title" class="pcd-col__title">Product Code Distribution</h2>
      <div class="pcd-card">
        <div class="pcd-card__toolbar">
          <div class="pcd-search">
            <i class="fa-solid fa-magnifying-glass pcd-search__icon" aria-hidden="true"></i>
            <input
              type="search"
              class="pcd-search__input"
              placeholder="Search Product Codes"
              [(ngModel)]="productCodeSearch"
              (ngModelChange)="onProductSearchChange()"
              aria-label="Search product codes"
              autocomplete="off"
            />
          </div>
          <button type="button" class="pcd-icon-btn" aria-label="Filter product codes">
            <i class="fa-solid fa-filter" aria-hidden="true"></i>
          </button>
        </div>
        <div class="pcd-card__divider" aria-hidden="true"></div>
        <div class="pcd-bar-list">
          @for (sub of filteredSubcategories; track trackSub($index, sub)) {
          <button
            type="button"
            class="pcd-hbar-row"
            [class.pcd-hbar-row--active]="isSubSelected(sub)"
            (click)="selectSubcategory(sub, $event)"
          >
            <span class="pcd-hbar-row__label">{{ sub.code }}</span>
            <div class="pcd-hbar-row__track">
              <span
                class="pcd-hbar-row__fill"
                [style.width.%]="(sub.count / maxSubCount) * 100"
              ></span>
            </div>
            <span class="pcd-hbar-row__value">{{ sub.count | number }}</span>
          </button>
          }
        </div>
      </div>
    </section>

    <div class="pcd-col-divider" aria-hidden="true"></div>

    <!-- Firmware Distribution -->
    <section class="pcd-col pcd-col--firmware" aria-labelledby="pcd-firmware-dist-title">
      <h2 id="pcd-firmware-dist-title" class="pcd-col__title">Firmware Distribution</h2>
      <div class="pcd-card pcd-card--firmware">
        <div class="pcd-segmented" role="group" aria-label="Firmware filter">
          <button
            type="button"
            class="pcd-segmented__btn"
            [class.pcd-segmented__btn--active]="firmwareFilter === 'all'"
            (click)="setFirmwareFilter('all')"
          >
            All
          </button>
          <button
            type="button"
            class="pcd-segmented__btn"
            [class.pcd-segmented__btn--active]="firmwareFilter === 'latest'"
            (click)="setFirmwareFilter('latest')"
          >
            Latest
          </button>
          <button
            type="button"
            class="pcd-segmented__btn"
            [class.pcd-segmented__btn--active]="firmwareFilter === 'outdated'"
            (click)="setFirmwareFilter('outdated')"
          >
            Outdated
          </button>
        </div>

        <div class="pcd-donut-block">
          <div class="pcd-donut-wrap">
            <canvas id="productCodeDistributionDonut" width="220" height="220" role="img" aria-label="Firmware distribution chart"></canvas>
            <div class="pcd-donut-center">
              <span class="pcd-donut-center__text">{{ activeSub.code }}</span>
            </div>
          </div>
          <div class="pcd-firmware-legend">
            @for (row of firmwareLegendRows; track trackFw($index, row)) {
            <button
              type="button"
              class="pcd-legend-row"
              [class.pcd-legend-row--active]="isFirmwareSelected(row.slice)"
              (click)="onLegendRowClick(row.slice, $event)"
            >
              <span class="pcd-legend-dot" [style.background-color]="row.slice.color"></span>
              <span class="pcd-legend-name">{{ row.slice.id }}:</span>
              <span class="pcd-legend-pct">{{ row.pct }}%</span>
            </button>
            }
          </div>
        </div>

        <div class="pcd-fw-bars">
          @for (row of firmwareLegendRows; track trackFw($index, row)) {
          <div class="pcd-fw-bar-row">
            <span class="pcd-fw-bar-dot" [style.background-color]="row.slice.color"></span>
            <span class="pcd-fw-bar-label">{{ row.slice.id }}</span>
            <div class="pcd-fw-bar-track">
              <span
                class="pcd-fw-bar-fill"
                [style.width.%]="(row.slice.count / maxFirmwareBar) * 100"
              ></span>
            </div>
            <span class="pcd-fw-bar-count">{{ row.slice.count | number }}</span>
          </div>
          }
        </div>

        <div class="pcd-fw-summary" aria-label="Latest versus outdated firmware">
          <div class="pcd-fw-summary-row">
            <span class="pcd-fw-summary-dot pcd-fw-summary-dot--latest"></span>
            <span class="pcd-fw-summary-label">Latest</span>
            <div class="pcd-fw-summary-track">
              <span
                class="pcd-fw-summary-fill pcd-fw-summary-fill--latest"
                [style.width.%]="
                  activeSub.latestBar + activeSub.outdatedBar
                    ? (activeSub.latestBar / (activeSub.latestBar + activeSub.outdatedBar)) * 100
                    : 0
                "
              ></span>
            </div>
            <span class="pcd-fw-summary-val">{{ activeSub.latestBar | number }}</span>
          </div>
          <div class="pcd-fw-summary-row">
            <span class="pcd-fw-summary-dot pcd-fw-summary-dot--outdated"></span>
            <span class="pcd-fw-summary-label">Outdated</span>
            <div class="pcd-fw-summary-track">
              <span
                class="pcd-fw-summary-fill pcd-fw-summary-fill--outdated"
                [style.width.%]="
                  activeSub.latestBar + activeSub.outdatedBar
                    ? (activeSub.outdatedBar / (activeSub.latestBar + activeSub.outdatedBar)) * 100
                    : 0
                "
              ></span>
            </div>
            <span class="pcd-fw-summary-val">{{ activeSub.outdatedBar | number }}</span>
          </div>
        </div>
      </div>
    </section>

    <div class="pcd-col-divider" aria-hidden="true"></div>

    <!-- Configuration Distribution -->
    <section class="pcd-col pcd-col--config" aria-labelledby="pcd-config-dist-title">
      <h2 id="pcd-config-dist-title" class="pcd-col__title pcd-col__title--config">
        Configuration Distribution
      </h2>
      <div class="pcd-card pcd-card--config">
        <div class="pcd-card__toolbar pcd-card__toolbar--config">
          <div class="pcd-search">
            <i class="fa-solid fa-magnifying-glass pcd-search__icon" aria-hidden="true"></i>
            <input
              type="search"
              class="pcd-search__input"
              placeholder="Search Configurations"
              [(ngModel)]="configSearch"
              (ngModelChange)="onConfigSearchChange()"
              aria-label="Search configurations"
              autocomplete="off"
            />
          </div>
          <button type="button" class="pcd-icon-btn" aria-label="Filter configurations">
            <i class="fa-solid fa-filter" aria-hidden="true"></i>
          </button>
        </div>

        <div class="pcd-table-wrap">
          <table class="pcd-table">
            <thead>
              <tr>
                <th>Config</th>
                <th>Devices</th>
                <th>Percentage</th>
                <th>Last Used</th>
              </tr>
            </thead>
            <tbody>
              @for (row of configRowsPage; track trackCfg($index, row)) {
              <tr>
                <td>{{ row.config }}</td>
                <td>{{ row.devices | number }}</td>
                <td>{{ row.percentage }}</td>
                <td>{{ row.lastUsed }}</td>
              </tr>
              }
            </tbody>
          </table>
        </div>

        <div class="pcd-pagination">
          <span class="pcd-pagination__range">{{ configRangeLabel }}</span>
          <div class="pcd-pagination__controls">
            <button
              type="button"
              class="pcd-page-btn"
              aria-label="Previous page"
              [disabled]="configPage <= 1"
              (click)="onConfigPage(-1)"
            >
              <i class="fa-solid fa-chevron-left" aria-hidden="true"></i>
            </button>
            @for (n of configPageNumbers; track n) {
            <button
              type="button"
              class="pcd-page-num"
              [class.pcd-page-num--active]="n === configPage"
              (click)="goConfigPage(n)"
            >
              {{ n }}
            </button>
            }
            <button
              type="button"
              class="pcd-page-btn"
              aria-label="Next page"
              [disabled]="configPage >= configPageCount"
              (click)="onConfigPage(1)"
            >
              <i class="fa-solid fa-chevron-right" aria-hidden="true"></i>
            </button>
          </div>
        </div>
      </div>
    </section>
  </div>
</div>
